import jsPDF from "jspdf";
import autoTable from "jspdf-autotable";
import { supabase } from "../../Services/supabaseClient";

const formatMoney = (value) => {
  return `₦${Number(value || 0).toLocaleString()}`;
};

const safeText = (value) => {
  return String(value || "").trim();
};

const ensureSpace = (doc, y, neededHeight = 40) => {
  const pageHeight = doc.internal.pageSize.getHeight();

  if (y + neededHeight > pageHeight - 18) {
    doc.addPage();
    return 20;
  }

  return y;
};

const blobToDataUrl = (blob) =>
  new Promise((resolve, reject) => {
    const reader = new FileReader();

    reader.onloadend = () => resolve(reader.result);
    reader.onerror = reject;
    reader.readAsDataURL(blob);
  });

const loadImageAsDataUrl = async (url) => {
  if (!url) return null;

  try {
    const response = await fetch(url);
    if (!response.ok) return null;

    const blob = await response.blob();
    const dataUrl = await blobToDataUrl(blob);
    return dataUrl;
  } catch (error) {
    return null;
  }
};

const getCompanyInfo = async (companyId) => {
  if (!companyId) return null;

  const { data, error } = await supabase
    .from("company_info")
    .select("*")
    .eq("company_id", companyId)
    .maybeSingle();

  if (error) {
    throw error;
  }

  return data || null;
};

const addDraftWatermark = (doc, status) => {
  if (String(status || "").toLowerCase() !== "draft") return;

  const pageCount = doc.getNumberOfPages();
  const pageWidth = doc.internal.pageSize.getWidth();
  const pageHeight = doc.internal.pageSize.getHeight();

  for (let page = 1; page <= pageCount; page += 1) {
    doc.setPage(page);

    doc.saveGraphicsState();
    doc.setGState(new doc.GState({ opacity: 0.08 }));
    doc.setTextColor(51, 51, 51);
    doc.setFont("helvetica", "bold");
    doc.setFontSize(56);

    doc.text("DRAFT", pageWidth / 2, pageHeight / 2, {
      align: "center",
      angle: 35,
    });

    doc.restoreGraphicsState();
  }
};

export const generateQuotePdfDocument = async ({ quote, items }) => {
  const doc = new jsPDF({
    orientation: "portrait",
    unit: "mm",
    format: "a4",
  });

  const companyInfo = await getCompanyInfo(quote.company_id);
  const logoDataUrl = await loadImageAsDataUrl(companyInfo?.logo_url || "");

  const pageWidth = doc.internal.pageSize.getWidth();
  const marginX = 14;

  const companyName =
    safeText(companyInfo?.company_name) || "KostPadi Business";
  const companyEmail = safeText(companyInfo?.email) || "";
  const companyPhone = safeText(companyInfo?.phone) || "";
  const companyAddress = safeText(companyInfo?.address) || "";
  const companyTin = safeText(companyInfo?.tin || companyInfo?.TIN) || "";

  /* HEADER */
  doc.setFillColor(0, 128, 128);
  doc.rect(0, 0, pageWidth, 36, "F");

  doc.setFillColor(237, 179, 45);
  doc.circle(pageWidth - 16, 10, 13, "F");

  if (logoDataUrl) {
    try {
      doc.addImage(logoDataUrl, "PNG", 14, 8, 20, 20);
    } catch (error) {
      try {
        doc.addImage(logoDataUrl, "JPEG", 14, 8, 20, 20);
      } catch (innerError) {
        /* ignore invalid logo format */
      }
    }
  }

  doc.setFont("helvetica", "bold");
  doc.setFontSize(21);
  doc.setTextColor(255, 255, 255);
  doc.text("QUOTATION", logoDataUrl ? 40 : 14, 16);

  doc.setFont("helvetica", "normal");
  doc.setFontSize(10);
  doc.setTextColor(230, 248, 248);
  doc.text(companyName, logoDataUrl ? 40 : 14, 23);

  /* COMPANY DETAILS CARD */
  doc.setFillColor(255, 255, 255);
  doc.setDrawColor(225, 234, 234);
  doc.roundedRect(14, 42, 88, 34, 5, 5, "FD");

  doc.setFont("helvetica", "bold");
  doc.setFontSize(10);
  doc.setTextColor(0, 128, 128);
  doc.text("From", 18, 50);

  doc.setFont("helvetica", "bold");
  doc.setFontSize(12);
  doc.setTextColor(51, 51, 51);
  doc.text(companyName, 18, 57);

  doc.setFont("helvetica", "normal");
  doc.setFontSize(9.5);
  doc.setTextColor(95, 105, 105);

  const companyDetailLines = [
    companyEmail,
    companyPhone,
    companyAddress,
    companyTin ? `TIN: ${companyTin}` : "",
  ].filter(Boolean);

  doc.text(companyDetailLines, 18, 63);

  /* QUOTE META CARD */
  doc.setFillColor(248, 251, 251);
  doc.setDrawColor(225, 234, 234);
  doc.roundedRect(108, 42, 88, 34, 5, 5, "FD");

  doc.setFont("helvetica", "bold");
  doc.setFontSize(10);
  doc.setTextColor(0, 128, 128);
  doc.text("Quote Details", 112, 50);

  doc.setFont("helvetica", "bold");
  doc.setFontSize(9);
  doc.setTextColor(110, 120, 120);
  doc.text("Quote No", 112, 58);
  doc.text("Status", 112, 64);
  doc.text("Issue Date", 152, 58);
  doc.text("Valid Until", 152, 64);

  doc.setFont("helvetica", "bold");
  doc.setFontSize(10);
  doc.setTextColor(51, 51, 51);
  doc.text(safeText(quote.quote_number) || "N/A", 112, 61.5);
  doc.text(
    safeText(quote.status)
      ? safeText(quote.status).charAt(0).toUpperCase() +
          safeText(quote.status).slice(1)
      : "Draft",
    112,
    67.5
  );
  doc.text(safeText(quote.issue_date) || "N/A", 152, 61.5);
  doc.text(safeText(quote.valid_until) || "N/A", 152, 67.5);

  /* CUSTOMER + CONTACT CARDS */
  const cardY = 84;
  const cardW = 88;
  const cardH = 38;

  doc.setFillColor(255, 255, 255);
  doc.setDrawColor(228, 237, 237);
  doc.roundedRect(14, cardY, cardW, cardH, 5, 5, "FD");
  doc.roundedRect(108, cardY, cardW, cardH, 5, 5, "FD");

  doc.setFillColor(0, 128, 128);
  doc.roundedRect(18, cardY + 4, 28, 8, 3, 3, "F");
  doc.roundedRect(112, cardY + 4, 22, 8, 3, 3, "F");

  doc.setFont("helvetica", "bold");
  doc.setFontSize(9);
  doc.setTextColor(255, 255, 255);
  doc.text("CUSTOMER", 22, cardY + 9.5);
  doc.text("CONTACT", 116, cardY + 9.5);

  doc.setFont("helvetica", "bold");
  doc.setFontSize(12);
  doc.setTextColor(51, 51, 51);
  doc.text(safeText(quote.crm_accounts?.name) || "N/A", 18, cardY + 19);
  doc.text(safeText(quote.crm_contacts?.full_name) || "N/A", 112, cardY + 19);

  doc.setFont("helvetica", "normal");
  doc.setFontSize(10);
  doc.setTextColor(102, 112, 112);

  const customerEmail = safeText(quote.crm_accounts?.email) || "No email";
  const customerPhone = safeText(quote.crm_accounts?.phone) || "No phone";
  const contactEmail = safeText(quote.crm_contacts?.email) || "No email";
  const contactPhone = safeText(quote.crm_contacts?.phone) || "No phone";

  doc.text(doc.splitTextToSize(customerEmail, 76), 18, cardY + 26);
  doc.text(customerPhone, 18, cardY + 34);

  doc.text(doc.splitTextToSize(contactEmail, 76), 112, cardY + 26);
  doc.text(contactPhone, 112, cardY + 34);

  /* ITEMS TABLE */
  autoTable(doc, {
    startY: 130,
    margin: { left: marginX, right: marginX },
    head: [
      ["Item", "Description", "Qty", "Unit Price", "Disc", "Tax", "Line Total"],
    ],
    body: (items || []).map((item) => [
      safeText(item.item_name) || "N/A",
      safeText(item.description) || "-",
      Number(item.quantity || 0).toLocaleString(),
      formatMoney(item.unit_price),
      `${Number(item.discount_percent || 0).toLocaleString()}%`,
      `${Number(item.tax_percent || 0).toLocaleString()}%`,
      formatMoney(item.line_total),
    ]),
    headStyles: {
      fillColor: [0, 128, 128],
      textColor: [255, 255, 255],
      fontStyle: "bold",
      fontSize: 10,
      halign: "left",
      valign: "middle",
    },
    bodyStyles: {
      fontSize: 9.5,
      textColor: [51, 51, 51],
      cellPadding: 4,
      valign: "top",
      lineColor: [236, 242, 242],
      lineWidth: 0.2,
    },
    alternateRowStyles: {
      fillColor: [249, 252, 252],
    },
    styles: {
      overflow: "linebreak",
    },
    columnStyles: {
      0: { cellWidth: 32 },
      1: { cellWidth: 46 },
      3: { cellWidth: 25, halign: "left" },
      4: { cellWidth: 16, halign: "left" },
      2: { cellWidth: 14, halign: "left" },
      5: { cellWidth: 14, halign: "left" },
      6: { cellWidth: 27, halign: "left" },
    },
  });

  let y = doc.lastAutoTable ? doc.lastAutoTable.finalY + 10 : 145;
  y = ensureSpace(doc, y, 80);

  /* SUMMARY */
  const summaryW = 70;
  const summaryX = pageWidth - marginX - summaryW;
  const summaryY = y;

  doc.setFillColor(255, 255, 255);
  doc.setDrawColor(224, 232, 232);
  doc.roundedRect(summaryX, summaryY, summaryW, 40, 5, 5, "FD");

  doc.setFillColor(237, 179, 45);
  doc.roundedRect(summaryX, summaryY, summaryW, 10, 5, 5, "F");

  doc.setFont("helvetica", "bold");
  doc.setFontSize(10);
  doc.setTextColor(51, 51, 51);
  doc.text("QUOTE SUMMARY", summaryX + 5, summaryY + 6.8);

  doc.setFont("helvetica", "normal");
  doc.setFontSize(10);
  doc.setTextColor(90, 100, 100);

  doc.text("Subtotal", summaryX + 5, summaryY + 17);
  doc.text(formatMoney(quote.subtotal), summaryX + 65, summaryY + 17, {
    align: "right",
  });

  doc.text("Discount", summaryX + 5, summaryY + 23);
  doc.text(formatMoney(quote.discount_total), summaryX + 65, summaryY + 23, {
    align: "right",
  });

  doc.text("Tax", summaryX + 5, summaryY + 29);
  doc.text(formatMoney(quote.tax_total), summaryX + 65, summaryY + 29, {
    align: "right",
  });

  doc.setFont("helvetica", "bold");
  doc.setFontSize(11);
  doc.setTextColor(0, 128, 128);
  doc.text("Total", summaryX + 5, summaryY + 35);
  doc.text(formatMoney(quote.total), summaryX + 65, summaryY + 35, {
    align: "right",
  });

  /* NOTES */
  const leftBlockW = 116 - 30;
  const contentX = marginX;
  let notesY = y;

  doc.setFillColor(255, 255, 255);
  doc.setDrawColor(228, 237, 237);
  doc.roundedRect(contentX, notesY, leftBlockW, 18, 5, 5, "FD");

  doc.setFillColor(0, 128, 128);
  doc.roundedRect(contentX, notesY, 22, 8, 3, 3, "F");
  doc.setFont("helvetica", "bold");
  doc.setFontSize(9);
  doc.setTextColor(255, 255, 255);
  doc.text("NOTES", contentX + 5, notesY + 5.8);

  doc.setTextColor(85, 95, 95);
  doc.setFont("helvetica", "normal");
  doc.setFontSize(10);

  const noteLines = doc.splitTextToSize(
    safeText(quote.notes) || "No additional notes provided.",
    leftBlockW - 10
  );
  doc.text(noteLines, contentX + 5, notesY + 14);

  const notesHeight = Math.max(18, 10 + noteLines.length * 5 + 6);
  doc.setDrawColor(228, 237, 237);
  doc.roundedRect(contentX, notesY, leftBlockW, notesHeight, 5, 5, "S");

  y = Math.max(summaryY + 40, notesY + notesHeight) + 10;
  y = ensureSpace(doc, y, 55);

  /* TERMS */
  doc.setFillColor(255, 255, 255);
  doc.setDrawColor(228, 237, 237);
  doc.roundedRect(contentX, y, pageWidth - 28, 18, 5, 5, "FD");

  doc.setFillColor(0, 128, 128);
  doc.roundedRect(contentX, y, 22, 8, 3, 3, "F");
  doc.setFont("helvetica", "bold");
  doc.setFontSize(9);
  doc.setTextColor(255, 255, 255);
  doc.text("TERMS", contentX + 5, y + 5.8);

  doc.setTextColor(85, 95, 95);
  doc.setFont("helvetica", "normal");
  doc.setFontSize(10);

  const termLines = doc.splitTextToSize(
    safeText(quote.terms) || "Standard payment terms apply.",
    pageWidth - 38
  );
  doc.text(termLines, contentX + 5, y + 14);

  const termsHeight = Math.max(18, 10 + termLines.length * 5 + 6);
  doc.setDrawColor(228, 237, 237);
  doc.roundedRect(contentX, y, pageWidth - 28, termsHeight, 5, 5, "S");

  y = y + termsHeight + 12;
  y = ensureSpace(doc, y, 42);

  /* SIGNATURE / APPROVAL */
  doc.setFillColor(255, 255, 255);
  doc.setDrawColor(228, 237, 237);
  doc.roundedRect(14, y, pageWidth - 28, 30, 5, 5, "FD");

  doc.setFont("helvetica", "bold");
  doc.setFontSize(10);
  doc.setTextColor(51, 51, 51);
  doc.text("Approval / Signature", 18, y + 8);

  doc.setDrawColor(180, 190, 190);
  doc.line(18, y + 22, 90, y + 22);
  doc.line(110, y + 22, 182, y + 22);

  doc.setFont("helvetica", "normal");
  doc.setFontSize(9);
  doc.setTextColor(110, 120, 120);
  doc.text("Authorized Signature", 18, y + 27);
  doc.text("Customer Acceptance", 110, y + 27);

  /* FOOTER */
  const pageCount = doc.getNumberOfPages();

  addDraftWatermark(doc, quote.status);

  for (let page = 1; page <= pageCount; page += 1) {
    doc.setPage(page);

    const footerY = doc.internal.pageSize.getHeight() - 10;

    doc.setDrawColor(230, 238, 238);
    doc.line(marginX, footerY - 4, pageWidth - marginX, footerY - 4);

    doc.setFont("helvetica", "normal");
    doc.setFontSize(9);
    doc.setTextColor(120, 128, 128);

    doc.text(companyName, marginX, footerY);
    doc.text(`Page ${page} of ${pageCount}`, pageWidth - marginX, footerY, {
      align: "right",
    });
  }

  return doc;
};
